import { c as _c } from "react/compiler-runtime";
import { ProgressBar } from '@systemeio/components';
import { useCourse } from 'modules/courses/courses/hooks/use-course';
import { StudentProgressExportFailReasonEnum } from 'modules/courses/students/enums/student-progress-export-fail-reason-enum';
import { StudentProgressExportStatusEnum } from 'modules/courses/students/enums/student-progress-export-status-enum';
import { useCancelStudentProgressExport } from 'modules/courses/students/hooks/use-cancel-student-progress-export';
import { useStudentProgressExportStatusPolling } from 'modules/courses/students/hooks/use-student-progress-export-status-polling';
import type { NotificationStudentProgressExportItemInterface } from 'modules/courses/students/types/student-progress-export-notification-interface';
import { NotificationItemCancelButton, NotificationItemDescription, NotificationItemView, type NotificationItemVariant } from 'modules/notifications/components/notification-item-view';
import { getJobNotificationIcon } from 'modules/notifications/constants/notification-icons';
import { useRevalidateNotificationsOnJobEnd } from 'modules/notifications/hooks/use-revalidate-notifications-on-job-end';
import { type ActionMenuItemProps } from 'shared/components';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
interface StudentProgressExportNotificationProps {
  actions?: ActionMenuItemProps[];
  notification: NotificationStudentProgressExportItemInterface;
  variant?: NotificationItemVariant;
}
const StudentProgressExportNotificationItem = t0 => {
  const $ = _c(9);
  const {
    actions,
    notification: notificationItem,
    variant
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    cancelStudentProgressExport,
    isCancelling
  } = useCancelStudentProgressExport();
  const t1 = notificationItem.data.status === StudentProgressExportStatusEnum.Running;
  let t2;
  if ($[0] !== notificationItem.data.exportId || $[1] !== t1) {
    t2 = {
      exportId: notificationItem.data.exportId,
      allowFetch: t1
    };
    $[0] = notificationItem.data.exportId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    exportStatus,
    mutate
  } = useStudentProgressExportStatusPolling(t2);
  const notification = {
    ...notificationItem,
    data: {
      ...notificationItem.data,
      ...(exportStatus ?? {})
    }
  };
  const {
    courseData
  } = useCourse(notification.data.courseId ?? undefined);
  const isRunning = notification.data.status === StudentProgressExportStatusEnum.Running;
  useRevalidateNotificationsOnJobEnd(exportStatus !== undefined && exportStatus.status !== StudentProgressExportStatusEnum.Running);
  const onCancelExport = async () => {
    await cancelStudentProgressExport(notification.data.exportId);
    await mutate(_temp, false);
  };
  const getMessage = () => {
    switch (notification.data.status) {
      case StudentProgressExportStatusEnum.Running:
        {
          return t("dashboard.notifications.student_export.running");
        }
      case StudentProgressExportStatusEnum.Success:
        {
          return t("dashboard.notifications.student_export.success.title");
        }
      case StudentProgressExportStatusEnum.Failed:
        {
          switch (notification.data.failReason) {
            case StudentProgressExportFailReasonEnum.CancelledByUser:
              {
                return t("dashboard.notifications.student_export.failed.cancelled_by_user");
              }
            case StudentProgressExportFailReasonEnum.TimeExceeded:
              {
                return t("dashboard.notifications.student_export.failed.time_exceeded");
              }
            case StudentProgressExportFailReasonEnum.RowsNotFound:
              {
                return t("dashboard.notifications.student_export.failed.rows_not_found");
              }
          }
          return notification.message;
        }
      default:
        {
          return notification.message;
        }
    }
  };
  const getDescription = () => {
    const courseName = courseData?.name ? courseData.name : t("dashboard.notifications.student_export.all_courses");
    const info = t("dashboard.notifications.student_export.export_info", {
      courseName
    });
    switch (notification.data.status) {
      case StudentProgressExportStatusEnum.Running:
        {
          return <ProgressBar labelPosition="side" value={notification.data.progressPercent} />;
        }
      case StudentProgressExportStatusEnum.Failed:
        {
          switch (notification.data.failReason) {
            case StudentProgressExportFailReasonEnum.TimeExceeded:
              {
                return <NotificationItemDescription>{t("dashboard.notifications.student_export.failed.time_exceeded_description")}</NotificationItemDescription>;
              }
            case StudentProgressExportFailReasonEnum.RowsNotFound:
              {
                return <NotificationItemDescription>{t("dashboard.notifications.student_export.failed.rows_not_found_description")}</NotificationItemDescription>;
              }
            default:
              {
                return null;
              }
          }
        }
      case StudentProgressExportStatusEnum.Success:
        {
          return <NotificationItemDescription><span className="flex flex-col gap-1"><span>{info}</span><span className="text-text-primary">{t("dashboard.notifications.student_export.success.link")}</span></span></NotificationItemDescription>;
        }
      default:
        {
          return;
        }
    }
  };
  const t3 = notification.read;
  const t4 = getJobNotificationIcon(notification.data.failReason === StudentProgressExportFailReasonEnum.CancelledByUser ? "cancelled" : notification.data.status);
  const t5 = getMessage();
  let t6;
  if ($[3] !== isCancelling || $[4] !== isRunning || $[5] !== onCancelExport) {
    t6 = isRunning && <NotificationItemCancelButton isLoading={isCancelling} onClick={onCancelExport} />;
    $[3] = isCancelling;
    $[4] = isRunning;
    $[5] = onCancelExport;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== getDescription) {
    t7 = getDescription();
    $[7] = getDescription;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return <NotificationItemView variant={variant} isRead={t3} actions={actions} icon={t4} message={t5} time={isRunning ? notification.createdAt : notification.data.updatedAt} controls={t6}>{t7}</NotificationItemView>;
};
export default StudentProgressExportNotificationItem;
function _temp(data) {
  return data && {
    ...data,
    status: StudentProgressExportStatusEnum.Failed,
    failReason: StudentProgressExportFailReasonEnum.CancelledByUser
  };
}